import { SvgIcon, Typography } from "@mui/material";
import { forwardRef } from "react";
import { CoreSpinner } from "../../core";
import { ScreenReaderText } from "../../screenReader";
import i18nPackage from "../../i18n/i18n";
import Warning from "../../images/exclamation-filled.svg?react";
import Checkmark from "../../images/system-check-check.svg?react";
import styles from "./index.module.css";

// Copied substep status from lockdown
export type SubstepStatus = "unstarted" | "started" | "completed" | "failed";

// Made this incase in the future, someone wants all substeps to have these props
export type BaseSubstepProps = {
	status: SubstepStatus;
	/**We want each substep to scroll into focus so we need refs */
	rootRef?: React.Ref<HTMLDivElement>;
};

type BasicSubstepProps = {
	children: React.ReactNode;
	className?: string;
};

export const BasicSubstep = forwardRef<HTMLDivElement, BasicSubstepProps>(function BasicSubstep(
	{ children, className },
	ref,
) {
	return (
		<div className={`${styles.basicSubstepContainer} ${className ?? ""}`} ref={ref}>
			{children}
		</div>
	);
});

type BasicSubstepHeaderProps = {
	title: string;
	description: React.ReactNode;
	status: SubstepStatus;
	icon?: React.ReactNode;
};

export function BasicSubstepHeader({ title, description, status, icon }: BasicSubstepHeaderProps) {
	return (
		<div className={styles.basicSubstepHeaderWrapper}>
			{icon ?? getBasicSubstepIcon(status)}
			<div className={styles.basicSubstepTextWrapper}>
				<BasicSubstepTitle title={title} />
				<BasicSubstepDescription description={description} />
			</div>
		</div>
	);
}

export function BasicSubstepTitle({ title, className }: { title: string; className?: string }) {
	return (
		<Typography variant="h2" className={`${styles.basicSubstepTitle} ${className ? className : ""}`}>
			{title}
		</Typography>
	);
}

export function BasicSubstepDescription({
	description,
	className,
}: {
	description: React.ReactNode;
	className?: string;
}) {
	return (
		<Typography className={`${styles.basicSubstepDescription} ${className ? className : ""}`}>{description}</Typography>
	);
}

export function getBasicSubstepIcon(status: SubstepStatus) {
	if (status === "completed") {
		return (
			<>
				<SvgIcon sx={{ fontSize: 20, flexShrink: 0 }}>
					<Checkmark />
				</SvgIcon>
				<ScreenReaderText role="status">
					{i18nPackage.t("beforeQuiz.substepCompleted")}
				</ScreenReaderText>
			</>
		);
	} else if (status === "failed") {
		return (
			<SvgIcon sx={{ fontSize: 20, flexShrink: 0 }}  titleAccess={i18nPackage.t("beforeQuiz.substepFailed")}>
				<Warning />
			</SvgIcon>
		);
	} else if (status === "started") {
		return <CoreSpinner size={20} />;
	}
	return null;
}

export function combineSubstepStatuses(...statuses: SubstepStatus[]): SubstepStatus {
	const uniqueStatuses = new Set(statuses);
	if (uniqueStatuses.size === 0) {
		throw new Error("Statuses to combine is empty");
	} else if (uniqueStatuses.size === 1) {
		return statuses[0];
	} else if (statuses.includes("started")) {
		return "started";
	} else if (statuses.includes("failed")) {
		return "failed";
	} else {
		return "started";
	}
}
